<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <slot name="trigger" :openModal="openModal"></slot>
  <Teleport to="body">
    <transition
      enter-active-class=""
      enter-from-class="modal-opacity-0"
      enter-to-class="modal-opacity-100"
      leave-active-class=""
      leave-from-class="modal-opacity-100"
      leave-to-class="modal-opacity-0"
    >
      <div v-show="isOpen" :class="['base-model', modalClass]">
        <transition
          enter-active-class="slide-left-enter-active"
          enter-from-class="slide-left-enter"
          enter-to-class=""
          leave-active-class="slide-left-enter-active"
          leave-from-class=".slide-left-leave-to"
          leave-to-class="slide-left-leave-to"
        >
          <div
            v-show="isOpen"
            ref="modal"
            role="dialog"
            aria-modal="true"
            class="base-model__inner"
          >
            <div class="base-model__inner-wrapper">
              <transition
                enter-active-class="modal-opacity-0"
                enter-from-class="modal-opacity-0"
                enter-to-class="modal-opacity-100"
                leave-active-class="modal-opacity-100"
                leave-from-class="modal-opacity-100"
                leave-to-class="modal-opacity-0"
              >
                <slot
                  :name="activeSlot"
                  :closeModal="closeModal"
                  :openModal="openModal"
                ></slot>
              </transition>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import useClickOutside from '../../hooks/useClickOutside';
import windowScrollLockHandler from '../../hooks/scrollLock';
import { ref, watch, inject, defineEmits, onMounted } from 'vue';

const injectedIsOpen = inject('injectedIsOpen');
const emit = defineEmits(['update:state']);

const isOpen = ref(props.openOnMount);
const modal = ref(null);
const trigger = ref(null);
const activeSlot = ref('');

const focusableElementsSelector =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const { onClickOutside } = useClickOutside();
onClickOutside(
  modal,
  () => {
    if (isOpen.value === true) {
      closeModal();
    }
  },
  'userprefs-modal',
);

const openModal = (e, slotName, focusID) => {
  // If no slot name is provided we exit
  if (!slotName || slotName === '') {
    console.warn('No slot name provided');
    closeModal();
    return;
  }

  isOpen.value = true;
  activeSlot.value = slotName;

  windowScrollLockHandler(true);

  setTimeout(() => {
    let focusItem = null;
    if (focusID && focusID !== '') {
      focusItem = document.getElementById(focusID);
    } else {
      focusItem = modal.value.querySelector(focusableElementsSelector);
    }
    focusItem && focusItem.focus();
  }, 100);
};

const closeModal = (e) => {
  isOpen.value = false;
  
  windowScrollLockHandler(false);
  emit('update:state', 'isClosed');
};

const props = defineProps({
  modalClass: {
    type: String,
    default: '',
  },
  clickOutsideExcludeClass: {
    type: String,
    default: '',
  },
});

const lockNavigationHandler = (e) => {
  const focusableElements = modal.value.querySelectorAll(
    focusableElementsSelector,
  );
  if (
    e.key === 'Tab' &&
    !e.shiftKey &&
    document.activeElement === focusableElements[focusableElements.length - 1]
  ) {
    e.preventDefault();
    focusableElements[0].focus();
  } else if (
    e.key === 'Tab' &&
    e.shiftKey &&
    document.activeElement === focusableElements[0]
  ) {
    e.preventDefault();
    focusableElements[focusableElements.length - 1].focus();
  }
};

const setInertHandler = (bool) => {
  const pageContent = document.querySelectorAll('body > *:not(.base-model)');
  pageContent.forEach((element) => {
    if (bool) {
      element.setAttribute('inert', bool);
      element.setAttribute('aria-hidden', bool);
    } else {
      element.removeAttribute('inert');
      element.removeAttribute('aria-hidden');
    }
  });
};

watch(
  () => isOpen.value,
  () => {
    if (isOpen.value === true) {
      setTimeout(() => {
        setInertHandler(true);
        modal.value.addEventListener('keydown', (e) => {
          lockNavigationHandler(e);
        });
      }, 100);
    } else {
      modal.value.removeEventListener('keydown', (e) => {
        lockNavigationHandler(e);
      });
      setInertHandler(false);
      trigger.value?.focus();
    }
  },
);

watch(
  () => injectedIsOpen?.value,
  () => {
    isOpen.value = injectedIsOpen.value;
  },
);

onMounted(() => {
  // Safety check that the screen doesnt become locked
  setInertHandler(false);
});

</script>
